import { Alert, MenuItem, Stack } from '@mui/material';
import { FormDialog, FormTextField } from '../../../design-system';
import { CommonCatalogFields, commonDraftValid, type CommonCatalogDraft } from './ReferenceCatalogFields';
import type { ReferenceDataReference, ReferenceWorkSpace } from '../model/types';

export type WorkSpaceDraft = CommonCatalogDraft & {
  locationId: string;
};

export type WorkSpaceEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceWorkSpace;
  draft: WorkSpaceDraft;
};

export function workSpaceDraftValid(draft: WorkSpaceDraft, mode: WorkSpaceEditor['mode']) {
  return commonDraftValid(draft, mode) && Boolean(draft.locationId);
}

type WorkSpaceEditorDialogProps = {
  editor?: WorkSpaceEditor;
  busy: boolean;
  editorError?: string;
  locations: ReferenceDataReference[];
  onClose: () => void;
  onSubmit: () => void;
  onChange: <K extends keyof WorkSpaceDraft>(field: K, value: WorkSpaceDraft[K]) => void;
};

export function WorkSpaceEditorDialog({
  editor,
  busy,
  editorError,
  locations,
  onClose,
  onSubmit,
  onChange,
}: WorkSpaceEditorDialogProps) {
  return (
    <FormDialog
      open={Boolean(editor)}
      onClose={onClose}
      onSubmit={onSubmit}
      title={editor?.mode === 'create' ? '업무 공간 등록' : '업무 공간 수정'}
      cancelLabel="취소"
      submitLabel="변경사항 저장"
      busy={busy}
      busyLabel="저장 중…"
      submitDisabled={!editor || !workSpaceDraftValid(editor.draft, editor.mode)}
      maxWidth="sm"
    >
      {editor ? (
        <Stack spacing={2}>
          {editorError ? (
            <Alert severity="error" role="alert">
              {editorError}
            </Alert>
          ) : null}
          <CommonCatalogFields
            draft={editor.draft}
            mode={editor.mode}
            busy={busy}
            descriptorHelper="선택한 근무지 안에서 구성원을 배치할 세부 공간 이름입니다."
            onChange={(field, value) => onChange(field, value)}
          />
          <FormTextField
            select
            required
            label="근무지"
            value={editor.draft.locationId}
            disabled={busy}
            error={!editor.draft.locationId}
            onChange={(event) => onChange('locationId', event.target.value)}
          >
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.descriptor} · {location.code}
              </MenuItem>
            ))}
          </FormTextField>
        </Stack>
      ) : null}
    </FormDialog>
  );
}
